import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable, tap, catchError, throwError } from 'rxjs';

export interface UserViewData {
  userId: string;
  userData: {
    userId: string;
    userData: {
      role?: string;
      departments?: string[];
      sites?: any[];
      legalEntities?: string[];
      contractTypes?: string[];
      databaseOrganizations?: string[];
      defaultViews?: {
        [entityType: string]: any;
      };
      views?: {
        [entityType: string]: any[];
      };
    };
  };
}

export interface ViewSaveRequest {
  userId: string;
  viewId: string;
  viewData: any;
}

export interface ViewRemoveRequest {
  userId: string;
  viewName: string;
  entityType: string;
}

@Injectable({
  providedIn: 'root'
})
export class ViewsService {
  private http = inject(HttpClient);

  TOOLBAR_CONTRACT_LIST_SESSION_VIEW = null;
  TOOLBAR_CONTRACT_LIST_SESSION_FILTERS = null;

  /**
   * Gets the complete user view data including default views and named views.
   * GET: {URL_USERS_NT_VIEW_USER_VIEW}/{userId}
   *
   * @param userId The user's ID
   * @returns Observable of UserViewData containing all user view information
   */
  getUserView(userId: string): Observable<UserViewData> {
    const url = `${environment.URL_USERS_NT_VIEW_USER_VIEW}${userId}`;

    const options = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    return this.http.get<UserViewData>(url, options).pipe(
      // tap(response => console.log('Successfully fetched user view data:', response)),
      catchError(error => {
        console.error('Error fetching user view data:', error);
        return throwError(() => new Error('Could not fetch user view data.'));
      })
    );
  }

  /**
   * Saves a list view (either default view or named view).
   * PUT: {URL_USERS_NT_VIEW_SAVE_LIST_VIEW}
   *
   * Body format:
   * - For default view: { userId, viewId: "contracts", viewData: {...} }
   * - For named view: { userId, viewId: "contracts.MyViewName", viewData: {...} }
   *
   * @param org The organization identifier
   * @param userId The user's ID
   * @param viewId The view identifier (entityType or entityType.viewName)
   * @param viewData The view data to save (ag-grid column state, filters, etc.)
   * @returns Observable of the save response
   */
  saveListView(org: string, userId: string, viewId: string, viewData: any): Observable<any> {
    const body: ViewSaveRequest = {
      userId,
      viewId,
      viewData
    };

    const options = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    // console.log('Saving list view:', { org, userId, viewId, viewData });

    return this.http.put(environment.URL_USERS_NT_VIEW_SAVE_LIST_VIEW, body, options).pipe(
      // tap(response => console.log('Successfully saved list view:', response)),
      catchError(error => {
        console.error('Error saving list view:', error);
        return throwError(() => new Error('Could not save list view.'));
      })
    );
  }

  /**
   * Removes a custom named view from the user's saved views.
   * PUT: {URL_USERS_NT_VIEW_REMOVE}
   *
   * Body format: { userId, viewName, entityType }
   *
   * Note: This only removes custom named views, NOT default views.
   *
   * @param org The organization identifier
   * @param userId The user's ID
   * @param viewName The name of the view to remove
   * @param entityType The entity type (e.g., "contracts", "people", etc.)
   * @returns Observable of the remove response
   */
  deleteCustomView(org: string, userId: string, viewName: string, entityType: string): Observable<any> {
    const body: ViewRemoveRequest = {
      userId,
      viewName,
      entityType
    };

    const options = {
      headers: {
        'Content-Type': 'application/json'
      }
    };

    // console.log('Removing custom view:', { org, userId, viewName, entityType });

    return this.http.put(environment.URL_USERS_NT_VIEW_REMOVE, body, options).pipe(
      // tap(response => console.log('Successfully removed custom view:', response)),
      catchError(error => {
        console.error('Error removing custom view:', error);
        return throwError(() => new Error('Could not remove custom view.'));
      })
    );
  }
}
